import React from 'react';
import PropTypes from 'prop-types';

import ImgDirectCircuit from 'img/project-design/direct-circuit.svg';
import ImgMixedCircuit from 'img/project-design/mixed-circuit.svg';
import ImgShowerhead from 'img/project-design/showerhead.svg';

import ImgSolarCollectorInRoof from 'img/project-design/solarcollector-in-roof.svg';
import ImgSolarCollectorOnRoof from 'img/project-design/solarcollector-on-roof.svg';
import ImgSolarCollectorHorizontalRoof from 'img/project-design/solarcollector-h-roof.svg';

import ImgExtUnit from 'img/project-design/external_unit.png';
import ImgPump from 'img/project-design/pump.png';
import ImgKettle from 'img/project-design/kettle.png';
import ImgExhaust from 'img/project-design/exhaust.png';
import ImgBoiler from 'img/project-design/boiler.png';
import ImgHydraulics from 'img/project-design/controlbox.png';
import ImgControls from 'img/project-design/controls.png';

import {
  SOLAR_COLLECTOR_TYPES,
} from 'utils/project-definition/config-item-info';

import { getElementAlign, getElementRectangle } from 'utils/project-definition/config-layout';

// TODO: remove fallback background color in production
// Should only be used to show components without image
const FALLBACK_BACKGROUND_COLOR = 'rgba(192, 192, 192, 1)';

const IMAGE_BY_TYPE = {
  external_unit: ImgExtUnit,
  pump: ImgPump,
  kettle: ImgKettle,
  exhaust: ImgExhaust,
  boiler: ImgBoiler,
  hydraulics: ImgHydraulics,
  control: ImgControls,
};


export default class ConfigComponent extends React.Component {

  /**
  * @method collectConfigObjectImageURLs
  * @static
  * @description Collects the image urls associated with this config component
  *   (Usually there is one image per config component, maybe in the future will be more ?)
  * @see config-view.jsx
  * @param {object} objectData - data of the config component (config product data, product info, ...)
  * @param {array} resultArray - Optional array where the url(s) will be added. If null, a new array will be created and returned
  * @return {array} - Array containing the component urls. Same array as the resultArray param if not null.
  */
  static collectConfigObjectImageURLs(objectData, resultArray = null) {
    const imgURL = this.findConfigObjectImageURL(objectData);
    if (!imgURL) {
      return resultArray;
    }
    const res = resultArray || [];
    if (res.indexOf(imgURL, 0) < 0) {
      res.push(imgURL);
    }
    return res;
  }

  /**
  * @method findConfigObjectImageURL
  * @static
  * @private
  * @description - Returns the image url of a component data object
  * @param {object} objectData - data of the config component (config product data, product info, ...)
  * @return {string} - component image url
  */
  static findConfigObjectImageURL(objectData) {
    if (!objectData) {
      return null;
    }
    const {
      type,
      productInfo,

      solarCollectorType,
      circuitType,
    } = objectData;

    let imgURL;

    const isSolarKit = (type === 'solar-kit' || type === 'solar_collector');

    // Don't use the image url of the solar kit product object, use the hard-coded images instead
    if (productInfo && productInfo.image && !isSolarKit) {
      return productInfo.image;
    }

    if (type === 'circuit') {
      imgURL = ImgMixedCircuit;
      if (objectData.originalSystem && objectData.originalSystem.data.attributes.heat_pump_series === 'Azorra Ace') {
        imgURL = ImgShowerhead;
      } else if (circuitType && circuitType.direct) {
        imgURL = ImgDirectCircuit;
      }
    } else if (isSolarKit) {
      imgURL = ImgSolarCollectorOnRoof;
      if (solarCollectorType === SOLAR_COLLECTOR_TYPES.IN_ROOF) {
        imgURL = ImgSolarCollectorInRoof;
      } else if (solarCollectorType === SOLAR_COLLECTOR_TYPES.HORIZONTAL_ROOF) {
        imgURL = ImgSolarCollectorHorizontalRoof;
      }
    }

    if (!imgURL) {
      imgURL = IMAGE_BY_TYPE[type];
    }

    return imgURL;
  }

  constructor(props) {
    super(props);

    this.clickComponentHandler = this.clickComponentHandler.bind(this);
    this.imageLoadedHandler = this.imageLoadedHandler.bind(this);
    this.imageRefHandler = this.imageRefHandler.bind(this);
  }

  // Other methods

  // Returns the rectangle of this component
  // = The local rectangle of this component's source data (calculated by getElementRectangle) + the position of this component
  // Used to render the selection rectangle
  getRectangle() {
    const { localRectangle } = this;
    if (!localRectangle) {
      return null;
    }
    const rectangle = this.rectangle || {};
    this.rectangle = rectangle;

    rectangle.x = localRectangle.x + this.props.x;
    rectangle.y = localRectangle.y + this.props.y;
    rectangle.width = localRectangle.width;
    rectangle.height = localRectangle.height;

    const { imageBounds } = this;
    if (imageBounds) {
      rectangle.x += imageBounds.x;
      rectangle.y += imageBounds.y;
      rectangle.width = imageBounds.width;
      rectangle.height = imageBounds.height;
    }

    return rectangle;
  }

  // Usually components only have an image, not a background-color
  // For debug purposes, a background color is shown if there is no image specified
  // Also useful to visualise the boundaries of a component
  getBackgroundColor() {
    const { objectData } = this.props;

    if (!objectData) {
      return null;
    }

    const url = this.getComponentImageURL();
    if (url) {
      // No background color, using an image
      return null;
    }
    return FALLBACK_BACKGROUND_COLOR;
  }

  calcLocalRect() {
    const { objectData } = this.props;
    this.localRectangle = getElementRectangle(objectData, this.localRectangle);

    this.localRectangle.y *= -1;

    this.localRectangle.width += 2;
    this.localRectangle.height += 2;

    return this.localRectangle;
  }

  // TODO: reuse more code - updateImageStyle & updateStyleOfImage contain too much duplicate code
  // updateImageStyle is called from render
  updateImageStyle(styleObject, image = null) {
    const style = styleObject;
    const img = image || this.imageRef;

    let width = this.imageWidth;
    let height = this.imageHeight;

    if (img) {
      if (!width) {
        width = img.naturalWidth || img.width;
      }
      if (!height) {
        height = img.naturalHeight || img.height;
      }
    }

    const { width: localRectWidth, height: localRectHeight } = this.calcLocalRect();
    const imageScaleX = width === 0 ? 0 : localRectWidth / width;
    const imageScaleY = height === 0 ? 0 : localRectHeight / height;
    const imageScale = imageScaleX < imageScaleY ? imageScaleX : imageScaleY;
    const imageScaledWidth = width * imageScale;
    const imageScaledHeight = height * imageScale;
    const objectAlign = getElementAlign(this.props.objectData) || { x: 0.5, y: 0.5 };
    const { x: alignX, y: alignY } = objectAlign;

    const x = (localRectWidth * alignX) - (imageScaledWidth * alignX);
    const y = (localRectHeight * alignY) - (imageScaledHeight * alignY);

    style.left = `${Math.round(x)}px`;
    style.top = `${Math.round(y)}px`;
    style.width = `${Math.round(imageScaledWidth)}px`;
    style.height = `${Math.round(imageScaledHeight)}px`;
    if (img && width > 0 && height > 0) {
      style.visibility = '';
    }
  }

  // updateStyleOfImage is called after loading the image
  updateStyleOfImage(img) {
    if (!img) {
      return;
    }
    const width = img.naturalWidth || img.width;
    const height = img.naturalHeight || img.height;
    this.imageWidth = width;
    this.imageHeight = height;

    const { width: localRectWidth, height: localRectHeight } = this.calcLocalRect();
    const imageScaleX = width === 0 ? 0 : localRectWidth / width;
    const imageScaleY = height === 0 ? 0 : localRectHeight / height;
    const imageScale = imageScaleX < imageScaleY ? imageScaleX : imageScaleY;
    const imageScaledWidth = width * imageScale;
    const imageScaledHeight = height * imageScale;
    const objectAlign = getElementAlign(this.props.objectData) || { x: 0.5, y: 0.5 };
    const { x: alignX, y: alignY } = objectAlign;

    const x = (localRectWidth * alignX) - (imageScaledWidth * alignX);
    const y = (localRectHeight * alignY) - (imageScaledHeight * alignY);
    const style = img.style;

    const imageBounds = this.imageBounds || {};
    this.imageBounds = imageBounds;

    imageBounds.x = x;
    imageBounds.y = y;
    imageBounds.width = imageScaledWidth;
    imageBounds.height = imageScaledHeight;

    const { objectData } = this.props;
    objectData.imageInfo = {
      imageBounds,
      width,
      height,
    };

    style.left = `${Math.round(x)}px`;
    style.top = `${Math.round(y)}px`;
    style.width = `${Math.round(imageScaledWidth)}px`;
    style.height = `${Math.round(imageScaledHeight)}px`;
    style.visibility = '';
  }

  // Event handlers

  imageRefHandler(img) {
    this.imageRef = img;
    // Image style should actually be defined using jsx syntax in the render method,
    // but this way it's faster to reuse code
    this.updateStyleOfImage(img);
  }

  imageLoadedHandler(evt) {
    const img = evt.target;
    this.updateStyleOfImage(img);

    const { onImageLoaded } = this.props;
    if (onImageLoaded) {
      onImageLoaded(img, this);
    }
  }

  clickComponentHandler(evt) {
    const { onClickComponent, objectData } = this.props;
    if (!onClickComponent) {
      return;
    }

    onClickComponent(objectData, this, evt);
  }

  getComponentImageURL() {
    const { objectData } = this.props;
    return ConfigComponent.findConfigObjectImageURL(objectData);
  }

  // Render methods

  renderContent() {
    const { objectData } = this.props;

    if (!objectData) {
      return null;
    }

    const imgURL = this.getComponentImageURL();

    let onClickComponent = null;
    if (this.props.onClickComponent) {
      onClickComponent = this.clickComponentHandler;
    }

    if (imgURL) {
      const imgStyle = {
        visibility: 'hidden',
      };
      this.updateImageStyle(imgStyle);
      return (
        <div className="config-view__component-image-container">
          <div
            onMouseDown={onClickComponent}
            onTouchStart={onClickComponent}
            role="button"
          >
            <img
              className="config-view__component-image"
              style={imgStyle}
              src={imgURL}
              alt={objectData.name}
              onLoad={this.imageLoadedHandler}
              ref={this.imageRefHandler}
            />
          </div>
        </div>
      );
      /*
      const style = {
        backgroundImage: `url(${imgURL})`,
      };
      return (
        <div className="config-view__component-image" style={style} />
      );
      */
    }

    return objectData.name;
  }

  renderDebugRect() {
    const style = {
      border: '#00FF00 1px solid',
      backgroundColor: '#000000',
      width: '5px',
      height: '5px',
      position: 'absolute',
      transform: 'translate(-50%, -50%)',
    };
    return (
      <div style={style} />
    );
  }

  render() {
    const { objectData, x, y } = this.props;

    if (!objectData) {
      return null;
    }

    const { type } = objectData;
    let rectExpansion = 0;
    if (type === 'circuit') {
      rectExpansion = 1;
    }

    const pixelX = Math.round(x);
    const pixelY = Math.round(y);

    // Position & other styles of the 'outer' element
    const style = {
      position: 'absolute',
      left: `${pixelX}px`,
      top: `${pixelY}px`,
      fontSize: '0.5em',
    };

    const { width, height } = objectData;

    // Calculate the rectangle of this component's data in local space
    // (relative to its own origin)
    const localRectangle = this.calcLocalRect();

    const localRectX = Math.round(localRectangle.x);
    const localRectY = Math.round(localRectangle.y);

    localRectangle.x = localRectX;
    localRectangle.y = localRectY;
    localRectangle.width = width;
    localRectangle.height = height;

    // Position & other styles of the 'inner' element (image container)
    const objectStyle = {
      position: 'absolute',
      left: `${localRectX - rectExpansion}px`,
      top: `${localRectY - rectExpansion}px`,
    };

    const bgColor = this.getBackgroundColor();
    if (bgColor) {
      objectStyle.backgroundColor = bgColor;
    }

    return (
      <div data-remeha-product-id={objectData.name} style={style}>
        <div style={objectStyle}>
          {this.renderContent()}
        </div>
      </div>
    );
  }
}

ConfigComponent.propTypes = {
  objectData: PropTypes.object,
  x: PropTypes.number,
  y: PropTypes.number,
  onClickComponent: PropTypes.func,
  onImageLoaded: PropTypes.func,
};



// WEBPACK FOOTER //
// ./js/components/configuration/config-component.jsx